import { combineReducers } from 'redux';
import type { ReduxModalState } from 'redux-modal';
import { reducer as modal } from 'redux-modal';

import type { ReducerState as ApiKeyCreateReducerState } from '../../features/account-detail/api-keys/api-keys-create/ducks';
import adminAccountApiKeyCreate from '../../features/account-detail/api-keys/api-keys-create/ducks';
import type { ReducerState as ApiKeyDetailReducerState } from '../../features/account-detail/api-keys/api-keys-detail/ducks';
import adminAccountApiKeyDetail from '../../features/account-detail/api-keys/api-keys-detail/ducks';
import type { ReducerState as ApiKeyListReducerState } from '../../features/account-detail/api-keys/api-keys-list/ducks';
import adminAccountApiKeyList from '../../features/account-detail/api-keys/api-keys-list/ducks';
import type { ReducerState as AdminAccountRoleDetailReducerState } from '../../features/account-detail/role-detail/ducks';
import adminAccountRoleDetail from '../../features/account-detail/role-detail/ducks';
import type { ReducerState as AdminAccountRoleListReducerState } from '../../features/account-detail/role-list/ducks';
import adminAccountRolesList from '../../features/account-detail/role-list/ducks';
import type { ReducerState as AccountRolodexReducerState } from '../../features/account-detail/rolodex/ducks';
import adminAccountRolodex from '../../features/account-detail/rolodex/ducks';
import type { ReducerState as AdminAccountSecurityReducerState } from '../../features/account-detail/security/ducks';
import adminAccountSecurity from '../../features/account-detail/security/ducks';
import type { ReducerState as AdminIdentityProviderReducerState } from '../../features/account-detail/security/identity-providers/ducks';
import adminAccountIdentityProviderDetail from '../../features/account-detail/security/identity-providers/ducks';
import type { ReducerState as AdminAccountReducerState } from '../../features/account-detail/shared/ducks';
import adminAccountDetail from '../../features/account-detail/shared/ducks';
import type { ReducerState as AdminAccountUserCreateReducerState } from '../../features/account-detail/user-create/ducks';
import adminAccountUserCreate from '../../features/account-detail/user-create/ducks';
import type { ReducerState as AdminAccountUserDetailReducerState } from '../../features/account-detail/user-detail/ducks';
import adminAccountUserDetail from '../../features/account-detail/user-detail/ducks';
import type { ReducerState as AdminAccountUserListReducerState } from '../../features/account-detail/user-list/ducks';
import adminAccountUserList from '../../features/account-detail/user-list/ducks';
import type { ReducerState as AdminAccountCreateReducerState } from '../../features/global-management/account-create/ducks';
import adminAccountCreate from '../../features/global-management/account-create/ducks';
import type { ReducerState as AdminAccountListReducerState } from '../../features/global-management/account-list/ducks';
import adminAccountList from '../../features/global-management/account-list/ducks';
import type { ReducerState as DraftAccountListReducerState } from '../../features/global-management/draft-account-list/ducks';
import draftAccountList from '../../features/global-management/draft-account-list/ducks';
import type { ReducerState as ChangePasswordReducerState } from '../../features/identity-management/change-password/ducks';
import changePassword from '../../features/identity-management/change-password/ducks';
import type { ReducerState as ConsentReducerState } from '../../features/identity-management/consent/ducks';
import consent from '../../features/identity-management/consent/ducks';
import type { ReducerState as FirstTimeActivationReducerState } from '../../features/identity-management/first-time-activation/ducks';
import firstTimeActivation from '../../features/identity-management/first-time-activation/ducks';
import type { ReducerState as ForgotPasswordReducerState } from '../../features/identity-management/forgot-password/ducks';
import forgotPassword from '../../features/identity-management/forgot-password/ducks';
import type { ReducerState as LoginReducerState } from '../../features/identity-management/login/ducks';
import login from '../../features/identity-management/login/ducks';
import type { ReducerState as ProfileReducerState } from '../../features/identity-management/profile/ducks';
import profile from '../../features/identity-management/profile/ducks';
import type { ReducerState as ResetPasswordReducerState } from '../../features/identity-management/reset-password/ducks';
import resetPassword from '../../features/identity-management/reset-password/ducks';
import type { ReducerState as ValidateInvestorSignupReducerState } from '../../features/investor-signup/ducks';
import validateInvestorSignup from '../../features/investor-signup/ducks';
import type { ReducerState as RolodexCrmIntegrationDetailReducerState } from '../../features/rolodex/crm-integration-detail/ducks';
import rolodexCrmIntegrationDetail from '../../features/rolodex/crm-integration-detail/ducks';
import type { ReducerState as RolodexCrmIntegrationListReducerState } from '../../features/rolodex/crm-integration-list/ducks';
import rolodexCrmIntegrationList from '../../features/rolodex/crm-integration-list/ducks';
import type { ReducerState as BulkImportFromCsvFileReducerState } from '../../features/rolodex/crm-management/crm-files-upload/ducks';
import bulkImportFromCsvFile from '../../features/rolodex/crm-management/crm-files-upload/ducks';
import type { ReducerState as RolodexCrmMatchListReducerState } from '../../features/rolodex/crm-match-list/ducks';
import rolodexCrmMatchList from '../../features/rolodex/crm-match-list/ducks';
import type { ReducerState as RolodexFirmDetailReducerState } from '../../features/rolodex/firm-detail/ducks';
import rolodexFirmDetail from '../../features/rolodex/firm-detail/ducks';
import type { ReducerState as RolodexFirmNewReducerState } from '../../features/rolodex/firm-new/ducks';
import rolodexFirmNew from '../../features/rolodex/firm-new/ducks';
import type { ReducerState as RolodexSharedReducerState } from '../../features/rolodex/shared/ducks';
import rolodexShared from '../../features/rolodex/shared/ducks';

export type RootState = {
  login: LoginReducerState;
  modal: ReduxModalState;
  adminAccountCreate: AdminAccountCreateReducerState;
  adminAccountList: AdminAccountListReducerState;
  adminAccountDetail: AdminAccountReducerState;
  adminAccountUserList: AdminAccountUserListReducerState;
  adminAccountUserDetail: AdminAccountUserDetailReducerState;
  adminAccountSecurity: AdminAccountSecurityReducerState;
  adminAccountUserCreate: AdminAccountUserCreateReducerState;
  adminAccountRolesList: AdminAccountRoleListReducerState;
  adminAccountRoleDetail: AdminAccountRoleDetailReducerState;
  draftAccountList: DraftAccountListReducerState;
  firstTimeActivation: FirstTimeActivationReducerState;
  forgotPassword: ForgotPasswordReducerState;
  resetPassword: ResetPasswordReducerState;
  changePassword: ChangePasswordReducerState;
  consent: ConsentReducerState;
  adminAccountIdentityProviderDetail: AdminIdentityProviderReducerState;
  profile: ProfileReducerState;
  adminAccountApiKeyList: ApiKeyListReducerState;
  adminAccountApiKeyDetail: ApiKeyDetailReducerState;
  adminAccountApiKeyCreate: ApiKeyCreateReducerState;
  adminAccountRolodex: AccountRolodexReducerState;
  rolodexFirmDetail: RolodexFirmDetailReducerState;
  rolodexFirmNew: RolodexFirmNewReducerState;
  rolodexShared: RolodexSharedReducerState;
  rolodexCrmIntegrationList: RolodexCrmIntegrationListReducerState;
  rolodexCrmIntegrationDetail: RolodexCrmIntegrationDetailReducerState;
  rolodexCrmMatchList: RolodexCrmMatchListReducerState;
  validateInvestorSignup: ValidateInvestorSignupReducerState;
  bulkImportFromCsvFile: BulkImportFromCsvFileReducerState;
};

const appReducer = combineReducers<RootState>({
  login,
  modal,
  adminAccountCreate,
  adminAccountList,
  adminAccountDetail,
  adminAccountUserList,
  adminAccountUserDetail,
  adminAccountSecurity,
  adminAccountUserCreate,
  adminAccountRolesList,
  adminAccountRoleDetail,
  draftAccountList,
  firstTimeActivation,
  forgotPassword,
  resetPassword,
  changePassword,
  consent,
  adminAccountIdentityProviderDetail,
  profile,
  adminAccountApiKeyList,
  adminAccountApiKeyDetail,
  adminAccountApiKeyCreate,
  adminAccountRolodex,
  rolodexFirmDetail,
  rolodexFirmNew,
  rolodexShared,
  rolodexCrmIntegrationList,
  rolodexCrmIntegrationDetail,
  rolodexCrmMatchList,
  validateInvestorSignup,
  bulkImportFromCsvFile,
});

export default appReducer;
